import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { useAllSubcounties, usePermission } from '../hooks';
import { colors } from '../theme/theme';
import { useSnackbar } from '../contexts/SnackbarContext';

export const SubcountiesPage = () => {
  const { can } = usePermission();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSubcounty, setSelectedSubcounty] = useState<any>(null);
  const [formName, setFormName] = useState('');

  const { subcounties, isLoading, error, createSubcounty, updateSubcounty, deleteSubcounty, isCreating, isUpdating } = useAllSubcounties({
    page: page + 1,
    per_page: rowsPerPage,
    search,
  });

  const handleOpenDialog = (subcounty?: any) => {
    if (subcounty) {
      setSelectedSubcounty(subcounty);
      setFormName(subcounty.name || '');
    } else {
      setSelectedSubcounty(null);
      setFormName('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSubcounty(null);
    setFormName('');
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      showSnackbar('Subcounty name is required', 'error');
      return;
    }

    try {
      if (selectedSubcounty) {
        await updateSubcounty({ id: selectedSubcounty.id, data: { name: formName } });
        showSnackbar('Subcounty updated successfully', 'success');
      } else {
        await createSubcounty({ name: formName });
        showSnackbar('Subcounty created successfully', 'success');
      }
      handleCloseDialog();
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || err?.message || 'Failed to save subcounty', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this subcounty?')) return;
    try {
      await deleteSubcounty(id);
      showSnackbar('Subcounty deleted successfully', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Failed to delete subcounty', 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'wards_count',
      headerName: 'Wards',
      width: 120,
      valueGetter: (value) => value || 0,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {can('locations.manage') && <IconButton size="small" onClick={() => handleOpenDialog(params.row)} sx={{ color: colors.primary.main }}>
            <Edit fontSize="small" />
          </IconButton>}
          {can('locations.manage') && <IconButton size="small" onClick={() => handleDelete(params.row.id)} sx={{ color: '#dc2626' }}>
            <Delete fontSize="small" />
          </IconButton>}
        </Box>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Subcounties"
          subtitle="Manage subcounties and their administrative boundaries"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'My County', path: '/county-profile' },
            { label: 'Subcounties' },
          ]}
          action={
            can('locations.manage') ? <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add Subcounty
            </Button> : undefined
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load subcounties. Please try again.
          </Alert>
        )}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search subcounties..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={subcounties?.data || []}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50]}
                  paginationModel={{ page, pageSize: rowsPerPage }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setRowsPerPage(model.pageSize);
                  }}
                  rowCount={subcounties?.meta?.total || subcounties?.total || 0}
                  paginationMode="server"
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedSubcounty ? 'Edit Subcounty' : 'Add Subcounty'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                autoFocus
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isCreating || isUpdating}
            >
              {(isCreating || isUpdating) ? <CircularProgress size={20} /> : (selectedSubcounty ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
